import { defineField, defineType } from "sanity";
import { PageTemplates } from "@/sanity/pages/templates";
import { PageTemplateSchema } from "@/sanity/templates";
import { GROQCTA } from "@/sanity/lib/definitions";
import { DEFAULT_LANGUAGE } from "@/sanity/globals/I18n";
import {
  appendTrackingParams,
  getTrackingParams,
  TrackingParams,
} from "@/lib/utm";

export const PageLink = defineType({
  title: "Page Link",
  name: "pageLink",
  type: "object",
  fields: [
    defineField({
      title: "External Link",
      name: "externalLink",
      type: "boolean",
      initialValue: true,
    }),
    defineField({
      name: "openInNewTab",
      title: "Open in new tab",
      type: "boolean",
      // @ts-ignore
      initialValue: (context) => context.parent?.externalLink || false,
    }),
    defineField({
      title: "External Link",
      name: "url",
      type: "url",
      placeholder: "https://www.google.com",
      initialValue: "",
      hidden: ({ parent }) => !parent?.externalLink,
      validation: (Rule) =>
        Rule.custom((link, context) => {
          if (
            typeof link === "undefined" &&
            // @ts-ignore
            context.parent?.externalLink === true
          ) {
            return "Please provide a link";
          }
          return true;
        }).error(),
    }),
    defineField({
      title: "Internal Link",
      name: "internalLink",
      type: "reference",
      // @ts-ignore
      to: [
        ...PageTemplates.map((template) => ({ type: template.documentType })),
      ],
      hidden: ({ parent }) => !!parent?.externalLink,
      validation: (Rule) =>
        Rule.custom((internalLink, context) => {
          if (
            typeof internalLink === "undefined" &&
            // @ts-ignore
            context.parent?.externalLink === false
          ) {
            return "Please select a page";
          }
          return true;
        }).error(),
    }),
    defineField({
      title: "Anchor",
      description: "CSS Selector",
      name: "anchor",
      type: "string",
      hidden: ({ parent }) => parent?.externalLink === true,
    }),
  ],
});

export interface IPageLink {
  externalLink: boolean;
  url: string;
  internalLink?: PageTemplateSchema;
  anchor?: string;
  openInNewTab: boolean;
}

const PROJECT_URL = "https://www.iqcapital.io";

/**
 * Checks if a URL points to any subdomain of iqcapital.io (any path)
 */
function isSubdomainLink(url: string): boolean {
  try {
    const { hostname } = new URL(url);
    const baseHostname = new URL(PROJECT_URL).hostname.replace(/^www\./, "");

    return hostname === baseHostname || hostname.endsWith(`.${baseHostname}`);
  } catch {
    return false;
  }
}

function isExternalLink(link: string) {
  return link.startsWith("http://") || link.startsWith("https://");
}

function addLanguagePrefix(path: string, language?: string) {
  const langPrefix =
    language && language !== DEFAULT_LANGUAGE ? `/${language}` : "";

  return !langPrefix || path === langPrefix || path.startsWith(`${langPrefix}/`)
    ? path
    : `${langPrefix}${path}`;
}

function addTrackingParams(
  link: string,
  linkType: "internal" | "external",
  trackingParams: TrackingParams,
) {
  // not adding tracking params for external links that are not subdomain links
  if (linkType === "external" && !isSubdomainLink(link)) {
    return link;
  }

  const parsedLink = linkType === "external" ? link : `${PROJECT_URL}${link}`;
  const enhancedLink = appendTrackingParams(parsedLink, trackingParams);

  return linkType === "external"
    ? enhancedLink.toString()
    : `${enhancedLink.pathname}${enhancedLink.search}${enhancedLink.hash}`;
}

export function resolveLink(link: string, language: string) {
  const trackingParams = getTrackingParams();

  const linkType = isExternalLink(link) ? "external" : "internal";

  if (Object.keys(trackingParams).length === 0) {
    return linkType === "internal" ? addLanguagePrefix(link, language) : link;
  }

  const enhancedLink = addTrackingParams(
    linkType === "internal" ? addLanguagePrefix(link, language) : link,
    linkType,
    trackingParams,
  );

  return enhancedLink;
}

export function resolveLinkUntracked(link: string, language: string): string {
  return isExternalLink(link) ? link : addLanguagePrefix(link, language);
}

export function resolvePageLink(link: IPageLink | null, language?: string) {
  if (!link) return null;

  if (link.externalLink) {
    // An external CTA saved without a URL must not crash the page.
    if (!link.url) return null;
    return resolveLink(link.url, language || DEFAULT_LANGUAGE);
  } else {
    // @ts-ignore
    const slug: string = link.internalLink?.settings?.slug?.current ?? "/";
    const anchor = link.anchor ? `#${link.anchor}` : "";
    const parsedLink = `${slug}${anchor}`;

    return resolveLink(parsedLink, language || DEFAULT_LANGUAGE);
  }
}

export function resolveCTA(cta: GROQCTA | null) {
  if (!cta) return null;

  if (cta.action === "link" && cta.link) {
    return cta.link;
  } else if (cta.action === "popup" && cta.popup) {
    return cta.popup;
  } else if (cta.action === "anchor" && cta.anchor) {
    return cta.anchor;
  }

  return null;
}

export const CTA = defineType({
  title: "Call to Action",
  name: "globalCTA",
  type: "document",
  fields: [
    defineField({
      title: "Name",
      name: "name",
      type: "string",
      initialValue: "Name",
      validation: (Rule) => Rule.required(),
    }),
    defineField({
      title: "Title",
      name: "title",
      type: "string",
      initialValue: "Title",
      validation: (Rule) => Rule.required(),
    }),
    defineField({
      name: "i18n",
      title: "Translations",
      type: "i18nText",
    }),
    defineField({
      name: "icon",
      title: "Icon",
      type: "string",
      description: "Select an icon for the CTA",
      options: {
        list: [
          { title: "None", value: "none" },
          { title: "Arrow Right", value: "arrowRight" },
          { title: "Document", value: "document" },
          { title: "WhatsApp", value: "whatsapp" },
        ],
        layout: "dropdown",
      },
      initialValue: "none",
    }),
    defineField({
      name: "action",
      title: "Action",
      type: "string",
      options: {
        list: [
          { title: "Link", value: "link" },
          { title: "Anchor", value: "anchor" },
          { title: "Popup", value: "popup" },
        ],
      },
      initialValue: "link",
      validation: (Rule) => Rule.required().error("Please select an action"),
    }),
    defineField({
      title: "Link",
      name: "link",
      type: "pageLink",
      hidden: ({ parent, value }) => parent?.action !== "link",
    }),
    defineField({
      title: "Anchor",
      name: "anchor",
      type: "string",
      hidden: ({ parent, value }) => parent?.action !== "anchor",
      validation: (Rule) =>
        Rule.custom((field, context) => {
          // @ts-ignore
          if (context.parent?.action === "anchor" && !field) {
            return "Please provide an anchor css selector";
          } else {
            return true;
          }
        }),
    }),
    defineField({
      name: "popup",
      title: "Popup",
      type: "reference",
      // @ts-ignore
      to: [{ type: "popup" }],
      hidden: ({ parent, value }) => parent?.action !== "popup",
      validation: (Rule) =>
        Rule.custom((field, context) => {
          // @ts-ignore
          if (context.parent?.action === "popup" && !field) {
            return "Please select a popup";
          } else {
            return true;
          }
        }).error(),
    }),
  ],
  preview: {
    select: {
      title: "name",
      subtitle: "title",
    },
  },
});

export const Button = defineType({
  title: "Button",
  name: "button",
  type: "object",
  options: {
    collapsible: true,
    collapsed: false,
  },
  fieldsets: [
    {
      title: "Style",
      name: "style",
      options: { collapsible: true, collapsed: false },
    },
  ],
  fields: [
    defineField({
      title: "Type",
      name: "type",
      type: "string",
      options: {
        list: [
          { title: "Default", value: "default" },
          { title: "White", value: "white" },
          { title: "Outline", value: "outline" },
          { title: "Ghost", value: "ghost" },
          { title: "Outline Black", value: "outline-black" },
          { title: "Green", value: "green" },
          { title: "Discord", value: "discord" },
          { title: "Dark Glow", value: "darkGlow" },
          { title: "Yellow", value: "yellow" },
          { title: "Default Old Design", value: "defaultOldDesign" },
          { title: "Light Green Old Design", value: "lightGreenOldDesign" },
        ],
      },
      initialValue: "default",
      validation: (Rule) =>
        Rule.required().error("Please select a button type"),
      fieldset: "style",
    }),
    defineField({
      title: "Size",
      name: "size",
      type: "string",
      options: {
        list: [
          { title: "Small", value: "sm" },
          { title: "Default", value: "default" },
          { title: "Large", value: "lg" },
          { title: "Default Old Design", value: "defaultOld" },
        ],
      },
      initialValue: "default",
      validation: (Rule) =>
        Rule.required().error("Please select a button size"),
      fieldset: "style",
    }),
    defineField({
      name: "shape",
      title: "Shape",
      type: "string",
      options: {
        list: [
          { title: "Round", value: "round" },
          { title: "Pill", value: "pill" },
          { title: "Square", value: "square" },
        ],
      },
      initialValue: "round",
      fieldset: "style",
      validation: (Rule) =>
        Rule.required().error("Please select a button shape"),
    }),
    defineField({
      title: "CTA",
      name: "cta",
      type: "reference",
      // @ts-ignore
      to: [{ type: "globalCTA" }],
    }),
  ],
});
